/**
 * Google Reviews Sync - Hosted on Shopify
 *
 * Fetches Google Reviews using the Maps JavaScript API + Places Library.
 * Tries Places API (New) first, then falls back to legacy PlacesService.
 */

(function() {
  'use strict';

  const CONFIG = window.GOOGLE_REVIEWS_CONFIG || {
    GOOGLE_API_KEY: 'AIzaSyB7YeEj8aT2wAtZhWNWm0AHQ64jEXThxKw',
    PLACE_ID: 'ChIJG8hPzIBz4YkRgf1letgZljU',
    MIN_RATING: 4,
    AUTO_SYNC: true,
    UPDATE_METAFIELD: false,
    METAFIELD_PROXY_URL: ''
  };

  const PREVIEW_DATA = {
    rating: 4.9,
    total_ratings: 287,
    reviews: [
      {
        author_name: 'Sarah M.',
        rating: 5,
        text: 'The staff took time to understand my running gait and found the perfect shoes. Best shoe shopping experience I have had.',
        time: '2 weeks ago',
        profile_photo_url: ''
      },
      {
        author_name: 'Mike R.',
        rating: 5,
        text: 'Locally owned and incredibly knowledgeable. They helped resolve foot pain I had been dealing with for months.',
        time: '1 month ago',
        profile_photo_url: ''
      },
      {
        author_name: 'Jennifer L.',
        rating: 5,
        text: 'Quick, friendly service and a great selection. Ted\'s is our go-to for the whole family.',
        time: '3 weeks ago',
        profile_photo_url: ''
      }
    ]
  };

  let mapsLoaderPromise = null;

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function getReviewText(review) {
    if (typeof review.text === 'string') return review.text;
    if (review.text && typeof review.text.text === 'string') return review.text.text;
    return '';
  }

  function getReviewRating(review) {
    return review.rating || review.starRating || 0;
  }

  function mapsApiIsReady(maps) {
    if (!maps) return false;
    if (typeof maps.importLibrary === 'function') return true;
    return !!(maps.places && maps.places.PlacesService);
  }

  function waitForMapsReady(timeoutMs) {
    timeoutMs = timeoutMs || 15000;

    return new Promise(function(resolve, reject) {
      const started = Date.now();

      (function poll() {
        if (typeof google !== 'undefined' && google.maps && mapsApiIsReady(google.maps)) {
          resolve(google.maps);
          return;
        }

        if (Date.now() - started >= timeoutMs) {
          reject(new Error('API_LOAD_TIMEOUT'));
          return;
        }

        setTimeout(poll, 100);
      })();
    });
  }

  async function loadMapsLibrary() {
    if (mapsLoaderPromise) return mapsLoaderPromise;

    mapsLoaderPromise = (async function() {
      if (typeof google !== 'undefined' && google.maps && mapsApiIsReady(google.maps)) {
        return google.maps;
      }

      const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');

      if (!existingScript) {
        await new Promise(function(resolve, reject) {
          const script = document.createElement('script');
          script.src = 'https://maps.googleapis.com/maps/api/js?key=' + CONFIG.GOOGLE_API_KEY + '&libraries=places&loading=async';
          script.async = true;
          script.defer = true;
          script.onerror = function() {
            reject(new Error('SCRIPT_LOAD_FAILED'));
          };
          script.onload = resolve;
          document.head.appendChild(script);
        });
      }

      return waitForMapsReady();
    })();

    return mapsLoaderPromise;
  }

  async function fetchWithNewPlacesApi(maps) {
    if (typeof maps.importLibrary !== 'function') {
      throw new Error('NEW_API_NOT_AVAILABLE');
    }

    const placesLibrary = await maps.importLibrary('places');
    const Place = placesLibrary.Place;

    if (!Place) {
      throw new Error('NEW_API_NOT_AVAILABLE');
    }

    const place = new Place({ id: CONFIG.PLACE_ID });

    await place.fetchFields({
      fields: ['displayName', 'rating', 'userRatingCount', 'reviews']
    });

    return {
      rating: place.rating || 0,
      user_ratings_total: place.userRatingCount || 0,
      reviews: place.reviews || []
    };
  }

  async function ensurePlacesLibrary(maps) {
    if (google.maps.places && google.maps.places.PlacesService) {
      return;
    }

    if (typeof maps.importLibrary === 'function') {
      await maps.importLibrary('places');
      return;
    }

    throw new Error('PLACES_LIBRARY_NOT_AVAILABLE');
  }

  async function fetchWithLegacyPlacesApi(maps) {
    await ensurePlacesLibrary(maps);

    const container = document.createElement('div');
    container.style.display = 'none';
    document.body.appendChild(container);

    try {
      const map = new maps.Map(container, {
        center: { lat: 42.9311, lng: -72.2789 },
        zoom: 15
      });

      return await new Promise(function(resolve, reject) {
        const service = new google.maps.places.PlacesService(map);

        service.getDetails(
          {
            placeId: CONFIG.PLACE_ID,
            fields: ['rating', 'user_ratings_total', 'reviews']
          },
          function(place, status) {
            if (status === google.maps.places.PlacesServiceStatus.OK && place) {
              resolve({
                rating: place.rating || 0,
                user_ratings_total: place.user_ratings_total || 0,
                reviews: place.reviews || []
              });
              return;
            }

            reject(new Error(status || 'LEGACY_FETCH_FAILED'));
          }
        );
      });
    } finally {
      container.remove();
    }
  }

  async function fetchPlaceReviews(maps) {
    try {
      return await fetchWithNewPlacesApi(maps);
    } catch (newApiError) {
      console.warn('Places API (New) unavailable, trying legacy PlacesService:', newApiError.message);
      return fetchWithLegacyPlacesApi(maps);
    }
  }

  async function initGoogleReviews() {
    if (isDesignMode()) {
      console.log('Theme Editor: showing preview Google Reviews');
      updatePageDisplay(PREVIEW_DATA);
      return;
    }

    try {
      const maps = await loadMapsLibrary();
      const placeData = await fetchPlaceReviews(maps);
      handleReviewsData(placeData);
    } catch (error) {
      console.error('Error fetching Google Reviews:', error);

      const section = document.querySelector('.google-reviews-section');
      if (hasVisibleReviews(section)) {
        console.warn('Keeping server-rendered fallback reviews');
        return;
      }

      const cached = localStorage.getItem('google_reviews_cache');
      if (cached) {
        try {
          updatePageDisplay(JSON.parse(cached).data);
          return;
        } catch (cacheError) {
          console.warn('Invalid reviews cache');
        }
      }

      handleError(error.message || 'FETCH_ERROR');
    }
  }

  function getReviewTimestamp(review) {
    if (review.publishTime instanceof Date) return review.publishTime.getTime();
    if (review.publishTime) {
      const parsed = new Date(review.publishTime);
      if (!isNaN(parsed.getTime())) return parsed.getTime();
    }
    if (typeof review.time === 'number') return review.time * 1000;
    if (review.time) {
      const parsed = new Date(review.time);
      if (!isNaN(parsed.getTime())) return parsed.getTime();
    }
    return 0;
  }

  function handleReviewsData(place) {
    const reviews = place.reviews || [];
    const overallRating = place.rating || 0;
    const totalRatings = place.user_ratings_total || 0;

    const filteredReviews = reviews
      .filter(function(review) {
        return getReviewRating(review) >= CONFIG.MIN_RATING;
      })
      .sort(function(a, b) {
        return getReviewTimestamp(b) - getReviewTimestamp(a);
      });

    const reviewsData = {
      rating: overallRating,
      total_ratings: totalRatings,
      reviews: filteredReviews.map(function(review) {
        return {
          author_name: review.authorAttribution?.displayName || review.author_name || 'Anonymous',
          rating: getReviewRating(review) || 5,
          text: getReviewText(review),
          time: review.publishTime ? formatTimeAgo(review.publishTime) : (review.relative_time_description || ''),
          profile_photo_url: review.authorAttribution?.photoUri || review.profile_photo_url || ''
        };
      }),
      last_updated: new Date().toISOString()
    };

    updatePageDisplay(reviewsData);

    if (CONFIG.UPDATE_METAFIELD && CONFIG.METAFIELD_PROXY_URL) {
      updateShopifyMetafield(reviewsData);
    }

    localStorage.setItem('google_reviews_cache', JSON.stringify({
      data: reviewsData,
      timestamp: Date.now()
    }));
  }

  function updatePageDisplay(reviewsData) {
    const section = document.querySelector('.google-reviews-section');
    if (!section) return;

    const ratingElement = section.querySelector('.rating-number');
    if (ratingElement) {
      ratingElement.textContent = reviewsData.rating.toFixed(1);
    }

    const countTextElement = section.querySelector('.reviews-count-text');
    if (countTextElement) {
      countTextElement.textContent = reviewsData.total_ratings + ' reviews on Google';
    }

    const aiSummaryCount = section.querySelector('.ai-summary-count');
    if (aiSummaryCount) {
      aiSummaryCount.textContent = reviewsData.total_ratings;
    }

    updateStars(section, reviewsData.rating);

    const carousel = section.querySelector('.google-reviews-carousel');
    if (!carousel) return;

    const placeholder = carousel.querySelector('.reviews-loading-placeholder');
    if (placeholder) placeholder.remove();

    carousel.querySelectorAll('.reviews-error, .google-reviews-error').forEach(function(node) {
      node.remove();
    });

    carousel.querySelectorAll('.google-review-card').forEach(function(card) {
      card.remove();
    });

    reviewsData.reviews.forEach(function(review) {
      carousel.appendChild(createReviewCard(review));
    });

    const sectionId = section.id.replace('google-reviews-', '');
    const manualReviews = window.MANUAL_REVIEWS_DATA && window.MANUAL_REVIEWS_DATA[sectionId];

    if (manualReviews && manualReviews.length > 0 && reviewsData.reviews.length === 0) {
      manualReviews.forEach(function(review) {
        carousel.appendChild(createReviewCard(review, true));
      });
    }

    const totalReviews = reviewsData.reviews.length || (manualReviews ? manualReviews.length : 0);

    if (totalReviews === 0) {
      const noReviews = document.createElement('div');
      noReviews.className = 'reviews-error';
      noReviews.innerHTML = '<p>No 4-5 star reviews available at this time.</p>';
      carousel.appendChild(noReviews);
      return;
    }

    if (typeof initReviewTruncation === 'function') {
      initReviewTruncation(sectionId);
    }
    if (typeof initReviewModal === 'function') {
      initReviewModal(sectionId);
    }
  }

  function updateStars(section, rating) {
    const starsContainer = section.querySelector('.rating-stars .stars-display');
    if (!starsContainer) return;

    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    const displayStars = hasHalf ? fullStars + 1 : fullStars;
    let starsHTML = '';

    for (let i = 1; i <= 5; i++) {
      if (i <= displayStars) {
        starsHTML += '<span style="color: var(--google-reviews-accent) !important;">★</span>';
      } else {
        starsHTML += '<span style="display: none;">☆</span>';
      }
    }

    starsContainer.innerHTML = starsHTML;
  }

  function createReviewCard(review, isManual) {
    isManual = !!isManual;

    const card = document.createElement('div');
    card.className = 'google-review-card';
    if (isManual) card.classList.add('manual-review');
    card.dataset.reviewId = Math.random().toString(36);

    const authorInitial = (review.author_name || 'A').charAt(0).toUpperCase();
    const stars = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
    const fullText = review.text || '';
    const maxLength = 200;
    const shouldTruncate = fullText.length > maxLength;
    const displayText = shouldTruncate ? fullText.substring(0, maxLength) + '...' : fullText;
    const showGoogleBadge = !isManual || (isManual && review.show_google_badge);

    const reviewData = {
      author: review.author_name || 'Anonymous',
      rating: stars,
      time: review.time || '',
      avatar: review.profile_photo_url || '',
      text: fullText,
      isManual: isManual,
      showGoogleBadge: showGoogleBadge
    };

    const googleBadgeHTML = showGoogleBadge ? `
          <div class="google-badge">
            <svg viewBox="0 0 24 24" fill="none" width="12" height="12">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
          </div>` : '';

    const verifiedBadgeHTML = showGoogleBadge ? `
            <svg class="review-verified" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>` : '';

    card.innerHTML = `
      <div class="review-card-header">
        <div class="review-avatar">
          ${review.profile_photo_url
            ? `<img src="${review.profile_photo_url}" alt="${review.author_name}" loading="lazy">`
            : `<span>${authorInitial}</span>`
          }
          ${googleBadgeHTML}
        </div>
        <div class="review-author-info">
          <div class="review-author-name">
            ${review.author_name}
            ${verifiedBadgeHTML}
          </div>
          ${review.time ? `<div class="review-time">${review.time}</div>` : ''}
        </div>
      </div>
      <div class="review-rating" style="color: var(--google-reviews-accent);">
        ${stars}
      </div>
      <div class="review-text ${shouldTruncate ? 'truncated' : ''}">${displayText}</div>
    `;

    if (shouldTruncate) {
      const reviewTextDiv = card.querySelector('.review-text');
      const readMoreBtn = document.createElement('button');
      readMoreBtn.className = 'read-more-btn';
      readMoreBtn.textContent = 'Read More';
      readMoreBtn.dataset.reviewData = JSON.stringify(reviewData);
      reviewTextDiv.parentElement.appendChild(readMoreBtn);
    }

    card.dataset.reviewData = JSON.stringify(reviewData);
    return card;
  }

  async function updateShopifyMetafield(reviewsData) {
    if (!CONFIG.METAFIELD_PROXY_URL) return;

    try {
      const response = await fetch(CONFIG.METAFIELD_PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reviews: reviewsData,
          storeDomain: window.Shopify?.shop || 'tedsports.myshopify.com'
        })
      });

      if (!response.ok) {
        console.warn('Failed to sync reviews to metafields');
      }
    } catch (error) {
      console.warn('Metafield sync failed:', error);
    }
  }

  function formatTimeAgo(dateOrTimestamp) {
    if (!dateOrTimestamp) return '';

    const date = dateOrTimestamp instanceof Date ? dateOrTimestamp : new Date(dateOrTimestamp);
    const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return diffDays + ' days ago';
    if (diffDays < 30) return Math.floor(diffDays / 7) + ' weeks ago';
    if (diffDays < 365) return Math.floor(diffDays / 30) + ' months ago';
    const years = Math.floor(diffDays / 365);
    return years === 1 ? '1 year ago' : years + ' years ago';
  }

  function hasVisibleReviews(section) {
    if (!section) return false;
    const carousel = section.querySelector('.google-reviews-carousel');
    return !!(carousel && carousel.querySelector('.google-review-card'));
  }

  function getFallbackData() {
    return {
      rating: parseFloat(CONFIG.FALLBACK_RATING) || 4.8,
      total_ratings: parseInt(CONFIG.FALLBACK_TOTAL, 10) || 287,
      reviews: []
    };
  }

  function handleError(status) {
    const section = document.querySelector('.google-reviews-section');
    if (!section) return;

    if (hasVisibleReviews(section)) {
      console.warn('Google Reviews API unavailable, keeping fallback reviews:', status);
      return;
    }

    const carousel = section.querySelector('.google-reviews-carousel');
    if (carousel) {
      const placeholder = carousel.querySelector('.reviews-loading-placeholder');
      if (placeholder) placeholder.remove();

      const countTextElement = section.querySelector('.reviews-count-text');
      if (countTextElement && countTextElement.textContent === 'Loading reviews...') {
        countTextElement.textContent = 'Reviews unavailable';
      }

      if (!carousel.querySelector('.reviews-error')) {
        const errorDiv = document.createElement('div');
        errorDiv.className = 'reviews-error';
        errorDiv.innerHTML = '<p>Unable to load reviews at this time.</p>';
        carousel.appendChild(errorDiv);
      }
    }

    console.error('Google Reviews error:', status);
  }

  function start() {
    if (!CONFIG.AUTO_SYNC) return;

    if (!isDesignMode()) {
      const cached = localStorage.getItem('google_reviews_cache');
      if (cached) {
        try {
          const cache = JSON.parse(cached);
          if (Date.now() - cache.timestamp < 60 * 60 * 1000) {
            updatePageDisplay(cache.data);
          }
        } catch (error) {
          console.warn('Invalid reviews cache');
        }
      }
    }

    initGoogleReviews();
  }

  window.syncGoogleReviews = function() {
    localStorage.removeItem('google_reviews_cache');
    mapsLoaderPromise = null;
    initGoogleReviews();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
